// ============================================
// CHÁMOMILE - Auth Modal Component
// Login, Register, Reset Password with Google Sign-in
// ============================================

import React, { useState } from 'react';
import {
    signUpWithEmail,
    signInWithEmail,
    signInWithGoogle,
    resetPassword
} from '../config/firebase';
import { linkAuthToMember } from '../services/loyaltyService';

// Premium Gold Color Palette (matches ChamomileApp)
const colors = {
    gold50: '#faf8f3', gold100: '#f5f0e6', gold200: '#ebe0cc', gold300: '#dccaa6',
    gold400: '#c9a962', gold500: '#b8943d', gold600: '#a07d2e', gold700: '#846628',
    stone50: '#fafaf9', stone100: '#f5f5f4', stone200: '#e7e5e4', stone300: '#d6d3d1',
    stone400: '#a8a29e', stone500: '#78716c', stone600: '#57534e', stone700: '#44403c',
    stone800: '#292524', stone900: '#1c1917',
    success: '#22c55e', error: '#ef4444',
};

const GoogleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
);

const AuthModal = ({ isOpen, onClose, onAuthSuccess, onShowTerms, onShowPrivacy }) => {
    const [view, setView] = useState('login'); // 'login' | 'register' | 'reset'
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [phone, setPhone] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    if (!isOpen) return null;

    const resetForm = () => {
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setDisplayName('');
        setPhone('');
        setError('');
        setSuccess('');
    };

    const switchView = (newView) => {
        setView(newView);
        resetForm();
    };

    // Link auth user to the unified members collection
    const saveCustomerProfile = async (user) => {
        try {
            await linkAuthToMember(user, phone, displayName);
        } catch (err) {
            console.error('Error linking auth to member:', err);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        if (!displayName.trim()) {
            setError('Please enter your full name.');
            return;
        }

        if (!phone.replace(/\D/g, '').trim() || phone.replace(/\D/g, '').length < 8) {
            setError('Please enter a valid phone number. We need this for your loyalty points!');
            return;
        }

        setLoading(true);
        try {
            const result = await signUpWithEmail(email, password, displayName);
            if (result.success && result.user) {
                await saveCustomerProfile(result.user);
                setSuccess('Account created! A verification email has been sent.');
                if (onAuthSuccess) onAuthSuccess(result.user);
                setTimeout(() => onClose(), 1500);
            } else {
                setError(result.error || 'Registration failed. Please try again.');
            }
        } catch (err) {
            setError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            const result = await signInWithEmail(email, password);
            if (result.success && result.user) {
                await saveCustomerProfile(result.user);
                setSuccess('Welcome back!');
                if (onAuthSuccess) onAuthSuccess(result.user);
                setTimeout(() => onClose(), 800);
            } else {
                setError(result.error || 'Login failed. Please try again.');
            }
        } catch (err) {
            setError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            const result = await signInWithGoogle();
            if (result.success && result.user) {
                await saveCustomerProfile(result.user);
                setSuccess('Signed in with Google!');
                if (onAuthSuccess) onAuthSuccess(result.user);
                setTimeout(() => onClose(), 800);
            } else {
                setError(result.error || 'Google sign-in failed.');
            }
        } catch (err) {
            setError('Google sign-in failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            const result = await resetPassword(email);
            if (result.success) {
                setSuccess('If an account exists with this email, a reset link has been sent.');
            } else {
                setError(result.error || 'Failed to send reset email.');
            }
        } catch (err) {
            setError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const inputStyle = {
        width: '100%',
        padding: '14px 16px',
        fontSize: '14px',
        border: `2px solid ${colors.stone200}`,
        borderRadius: '12px',
        outline: 'none',
        boxSizing: 'border-box',
        fontFamily: 'inherit',
        transition: 'all 0.15s ease',
        background: '#fff',
    };

    const labelStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '10px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: colors.stone500,
        marginBottom: '8px',
    };

    const primaryBtnStyle = {
        width: '100%',
        padding: '16px',
        fontSize: '15px',
        fontWeight: 700,
        color: '#fff',
        background: colors.stone900,
        border: 'none',
        borderRadius: '999px',
        cursor: loading ? 'not-allowed' : 'pointer',
        opacity: loading ? 0.7 : 1,
        transition: 'all 0.25s ease',
        fontFamily: 'inherit',
        letterSpacing: '0.3px',
    };

    const googleBtnStyle = {
        width: '100%',
        padding: '16px',
        fontSize: '14px',
        fontWeight: 600,
        color: colors.stone800,
        background: '#fff',
        border: `1.5px solid ${colors.stone200}`,
        borderRadius: '14px',
        cursor: loading ? 'not-allowed' : 'pointer',
        opacity: loading ? 0.7 : 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        transition: 'all 0.25s ease',
        fontFamily: 'inherit',
    };

    const linkBtnStyle = {
        background: 'none',
        border: 'none',
        color: colors.gold600,
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: 600,
        textDecoration: 'underline',
        padding: 0,
        fontFamily: 'inherit',
    };
    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.5)',
                backdropFilter: 'blur(8px)',
                zIndex: 200,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
            }}
            onClick={onClose}
        >
            <div
                style={{
                    background: '#fff',
                    borderRadius: '28px 28px 0 0',
                    padding: '32px 28px 40px',
                    maxWidth: '500px',
                    width: '100%',
                    maxHeight: '92vh',
                    overflowY: 'auto',
                    boxShadow: '0 -10px 40px rgba(0,0,0,0.15)',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close handle */}
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                    <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: colors.stone200 }} />
                </div>

                {/* Header */}
                <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                    <h2 style={{
                        fontSize: '26px',
                        fontWeight: 700,
                        color: colors.stone900,
                        fontFamily: '"Source Serif 4", Georgia, "Times New Roman", serif',
                        margin: '0 0 8px',
                        lineHeight: 1.2,
                    }}>
                        {view === 'login' ? 'Welcome to' : view === 'register' ? 'Join' : 'Reset Password'}
                        {view !== 'reset' && <><br /><span style={{ color: colors.gold400 }}>Chámomile</span></>}
                    </h2>
                    <p style={{ fontSize: '14px', color: colors.stone400, margin: 0, lineHeight: 1.5 }}>
                        {view === 'login'
                            ? 'Relaxation in every sip'
                            : view === 'register'
                                ? 'Create your account to start ordering'
                                : 'Enter your email to reset your password'}
                    </p>
                </div>

                {/* Error / Success Messages */}
                {error && (
                    <div style={{
                        padding: '12px 16px',
                        borderRadius: '12px',
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        color: colors.error,
                        fontSize: '13px',
                        marginBottom: '16px',
                    }}>
                        {error}
                    </div>
                )}
                {success && (
                    <div style={{
                        padding: '12px 16px',
                        borderRadius: '12px',
                        background: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        color: colors.success,
                        fontSize: '13px',
                        marginBottom: '16px',
                    }}>
                        {success}
                    </div>
                )}

                {/* Google Sign-in (Login & Register) — only Google */}
                {view !== 'reset' && (
                    <>
                        <button
                            onClick={handleGoogleSignIn}
                            disabled={loading}
                            style={googleBtnStyle}
                        >
                            <GoogleIcon />
                            Sign up with Google
                        </button>

                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            margin: '20px 0',
                        }}>
                            <div style={{ flex: 1, height: '1px', background: colors.stone200 }} />
                            <span style={{ fontSize: '12px', color: colors.stone400, fontWeight: 600 }}>Or</span>
                            <div style={{ flex: 1, height: '1px', background: colors.stone200 }} />
                        </div>
                    </>
                )}

                {/* Login Form */}
                {view === 'login' && (
                    <form onSubmit={handleLogin}>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>Email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="your@email.com"
                                required
                                disabled={loading}
                                style={inputStyle}
                            />
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                            <label style={labelStyle}>Password</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter your password"
                                required
                                disabled={loading}
                                style={inputStyle}
                            />
                        </div>
                        <div style={{ textAlign: 'right', marginBottom: '20px' }}>
                            <button type="button" onClick={() => switchView('reset')} style={linkBtnStyle}>
                                Forgot password?
                            </button>
                        </div>
                        <button type="submit" disabled={loading} style={primaryBtnStyle}>
                            {loading ? 'Signing in...' : 'Sign In'}
                        </button>
                        <p style={{ textAlign: 'center', fontSize: '13px', color: colors.stone500, marginTop: '20px' }}>
                            Don't have an account?{' '}
                            <button type="button" onClick={() => switchView('register')} style={linkBtnStyle}>
                                Sign up
                            </button>
                        </p>
                    </form>
                )}

                {/* Register Form */}
                {view === 'register' && (
                    <form onSubmit={handleRegister}>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>Full Name</label>
                            <input
                                type="text"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                placeholder="Your full name"
                                required
                                disabled={loading}
                                style={inputStyle}
                            />
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>Email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="your@email.com"
                                required
                                disabled={loading}
                                style={inputStyle}
                            />
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>Phone Number <span style={{ fontWeight: 400, textTransform: 'none', color: colors.stone400 }}>(required for Points)</span></label>
                            <input
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="e.g. 012-345 6789"
                                required
                                disabled={loading}
                                style={inputStyle}
                            />
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>Password</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Min 8 chars, letters & numbers"
                                required
                                disabled={loading}
                                style={inputStyle}
                            />
                        </div>
                        <div style={{ marginBottom: '20px' }}>
                            <label style={labelStyle}>Confirm Password</label>
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirm your password"
                                required
                                disabled={loading}
                                style={inputStyle}
                            />
                        </div>
                        <button type="submit" disabled={loading} style={primaryBtnStyle}>
                            {loading ? 'Creating account...' : 'Create Account'}
                        </button>
                        <p style={{ textAlign: 'center', fontSize: '13px', color: colors.stone500, marginTop: '20px' }}>
                            Already have an account?{' '}
                            <button type="button" onClick={() => switchView('login')} style={linkBtnStyle}>
                                Sign in
                            </button>
                        </p>
                    </form>
                )}

                {/* Reset Password Form */}
                {view === 'reset' && (
                    <form onSubmit={handleResetPassword}>
                        <div style={{ marginBottom: '20px' }}>
                            <label style={labelStyle}>Email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="your@email.com"
                                required
                                disabled={loading}
                                style={inputStyle}
                            />
                        </div>
                        <button type="submit" disabled={loading} style={primaryBtnStyle}>
                            {loading ? 'Sending...' : 'Send Reset Link'}
                        </button>
                        <p style={{ textAlign: 'center', fontSize: '13px', color: colors.stone500, marginTop: '20px' }}>
                            Remember your password?{' '}
                            <button type="button" onClick={() => switchView('login')} style={linkBtnStyle}>
                                Sign in
                            </button>
                        </p>
                    </form>
                )}

                {/* Footer Links (Terms & Privacy) */}
                <div style={{ textAlign: 'center', marginTop: '32px', borderTop: `1px solid ${colors.stone100}`, paddingTop: '24px' }}>
                    <p style={{ fontSize: '12px', color: colors.stone400, margin: 0 }}>
                        By proceeding, you agree to our{' '}
                        <button onClick={onShowTerms} style={{...linkBtnStyle, fontSize: '12px', color: colors.stone600}}>Terms of Service</button>
                        {' '}and{' '}
                        <button onClick={onShowPrivacy} style={{...linkBtnStyle, fontSize: '12px', color: colors.stone600}}>Privacy Policy</button>.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default AuthModal;
